import type { MeSessionSnapshot } from '@/features/auth/api/auth-types'
import { ClientPermissions } from '@/features/clients/permissions'
import { FinancePermissions } from '@/features/finance/permissions'
import { PayrollPermissions } from '@/features/payroll/permissions'
import { SelfAttendancePermissions } from '@/features/self-attendance/permissions'
import { SupervisorStaffDocsPermissions } from '@/features/supervisor-staff-docs/permissions'
import { LeavePermissions } from '@/features/leaves/permissions'
import { StockPermissions } from '@/features/stocks/permissions'
import { GatePassPermissions } from '@/features/gate-pass/permissions'
import {
  isSalesFeatureEnabled,
  SalesPermissions,
} from '@/features/sales/permissions'
import { StaffPersonalDetailsPermissions } from '@/features/staff-personal-details/permissions'
import { InterviewIntakePermissions } from '@/features/interview-intake/permissions'
import {
  canAdminQueryRecruitment,
  canManageOnboarding,
  isThinAdmin,
  isDocumentFilingOnly,
  isProDeskSuperAdmin,
  proEmployeeDirectoryNavAllowed,
  proExitWorkspaceAllowed,
  proOnboardingWorkspaceAllowed,
  shouldUseAdminPortal,
  staffCapabilitySet,
} from '@/shared/lib/staff-capabilities'

export type StaffNavContext = {
  showJobPostsNav: boolean
  showApplicantsNav: boolean
  showTalentPoolNav: boolean
  showWalkInNav: boolean
  walkInListedUnderRecruitment: boolean
  showRecruitmentSection: boolean
  showVisaOnboardingNav: boolean
  showWorkforceDirectory: boolean
  showPurgedEmployees: boolean
  showStaffing: boolean
  showTeam: boolean
  showAccessControl: boolean
  showPayroll: boolean
  showPayrollAdmin: boolean
  /** Accounts monthly-close workspace. */
  showAccountsWorkspace: boolean
  /** Finance ledger (petty cash, PEMO, employee-linked spend). */
  showFinanceNav: boolean
  /** Client invoicing workspace. */
  showInvoicingNav: boolean
  /** Supervisor / HR team attendance capture (approve_attendance). */
  showTeamAttendance: boolean
  /** Office team-reminder policies (not shown on the supervisor desk). */
  showAttendanceNotifications: boolean
  /** Leave / availability overview (retired from the Team sidebar). */
  showTeamAvailability: boolean
  /** Supervisor staff identity documents portal. */
  showSupervisorStaffDocs: boolean
  /** Supervisor desk list of people who have signed in on the mobile app. */
  showSupervisorAppSignIns: boolean
  /** Self-attendance exception review console. */
  showSelfAttendanceReview: boolean
  showLeavesNav: boolean
  /** Supervisor material request (stock_self_request). */
  showSupervisorMaterialRequest: boolean
  /** Supervisor gate pass requests (gate_pass_self_request). */
  showSupervisorGatePass: boolean
  /** Admin gate pass approval queue (gate_pass_approve). */
  showAdminGatePass: boolean
  /** HR gate pass issue queue (gate_pass_issue). */
  showHrGatePass: boolean
  /** Stocks / inventory workspace (uniforms, stationery, requisitions). */
  showStocksNav: boolean
  /** Stock Approvals admin desk (request/purchase approve). */
  showStockApprovalsNav: boolean
  /** Platform notification rules admin (ConfigureSystem). */
  showNotificationRulesNav: boolean
  showStaffPersonalDetailsNav: boolean
  /** Bulk upload of the supervisor staff sheet (staff_personal_details_import). */
  showStaffPersonalDetailsImportNav: boolean
  /** Walk-in interview applications (interview_intake_read / manage). */
  showInterviewIntakeNav: boolean
  showAdminSearch: boolean
  showClientIdentityNav: boolean
  /** Commercial contract register (manage). */
  showAdminContracts: boolean
  /** Commercial contract register (accounts read/manage). */
  showAccountsContracts: boolean
  /** Prospect pipeline, cases, accounts (non-production builds only). */
  showSalesNav: boolean
  showStaffOperations: boolean
  showContactInquiries: boolean
  showClientApplicants: boolean
  showProOnboarding: boolean
  showProExit: boolean
  /** Employee directory on the PRO desk. */
  showProWorkforceDirectory: boolean
  /** Mini admin panel. PRO owner only. */
  showProDeskAdmin: boolean
  /**
   * True when the Admin (platform settings) group would have children.
   * Derived only from flags that still live under Admin — not ops/payroll/team.
   */
  showAdminNav: boolean
  showAccountSection: boolean
  showMyAccessNav: boolean
  isClientOnly: boolean
  /**
   * Plain Administrator (not SystemAdministrator) — thin desk shell.
   * UX only; API still authorizes via capabilities.
   */
  isThinAdmin: boolean
  isDocumentFilingOnly: boolean
  showDocumentFilingSubmit: boolean
  showDocumentFilingAdmin: boolean
}

function hasCap(caps: Set<string>, key: string): boolean {
  return caps.has(key)
}

function hasAnyCap(caps: Set<string>, keys: readonly string[]): boolean {
  return keys.some((k) => caps.has(k))
}

/** Staff nav flags from session capabilities. */
export function buildStaffNavContext(
  snapshot: MeSessionSnapshot | undefined,
): StaffNavContext {
  const caps = new Set(snapshot?.capabilities ?? [])
  const capSet = staffCapabilitySet(snapshot?.capabilities ?? [])
  const platformAdmin = shouldUseAdminPortal(capSet)

  const canAdminRecruitment = canAdminQueryRecruitment(capSet)
  const canManageOnboardingCap = canManageOnboarding(capSet)
  const canManageJobPosts = hasCap(caps, 'manage_job_posts') || canAdminRecruitment
  const canAccessApplicants =
    hasCap(caps, 'manage_candidates') ||
    hasCap(caps, 'manage_interviews') ||
    canAdminRecruitment
  const canAccessHrWorkspace =
    canManageOnboardingCap ||
    hasCap(caps, 'onboarding_walk_in_create') ||
    hasCap(caps, 'onboarding_employee_access_manage') ||
    canAdminRecruitment ||
    hasCap(caps, 'manage_users')

  const showJobPostsNav = canManageJobPosts
  const showApplicantsNav = canAccessApplicants
  const showTalentPoolNav = showApplicantsNav
  const showWalkInNav =
    canManageOnboardingCap ||
    hasCap(caps, 'onboarding_walk_in_create') ||
    hasCap(caps, 'onboarding_employee_access_manage') ||
    canAdminRecruitment
  const showRecruitmentSection =
    showJobPostsNav || showApplicantsNav || showTalentPoolNav
  const showVisaOnboardingNav = canManageOnboardingCap || canAdminRecruitment
  const walkInListedUnderRecruitment =
    showWalkInNav && showRecruitmentSection && !showVisaOnboardingNav

  const showWorkforceDirectory = canAccessHrWorkspace
  const showPurgedEmployees = platformAdmin
  const showPayroll = hasCap(caps, PayrollPermissions.manage) || platformAdmin
  const showTeamAttendance =
    hasCap(caps, PayrollPermissions.approveAttendance) ||
    hasCap(caps, PayrollPermissions.manage) ||
    platformAdmin
  const showAttendanceNotifications = showTeamAttendance
  const showTeamAvailability = false
  const showSupervisorStaffDocs =
    platformAdmin || hasCap(caps, SupervisorStaffDocsPermissions.read)
  const showSelfAttendanceReview =
    platformAdmin || hasAnyCap(caps, SelfAttendancePermissions.access)
  const showStaffing =
    platformAdmin || hasCap(caps, 'manage_roles') || hasCap(caps, 'manage_users')
  const showAccessControl =
    platformAdmin ||
    hasCap(caps, 'manage_capabilities') ||
    hasCap(caps, 'manage_roles')
  const showTeam = showStaffing || showAccessControl
  const showPayrollAdmin =
    platformAdmin || hasAnyCap(caps, PayrollPermissions.adminConsoleAccess)
  const showAccountsWorkspace =
    platformAdmin || hasCap(caps, PayrollPermissions.accountsWorkspace)
  const showFinanceNav =
    platformAdmin || hasAnyCap(caps, FinancePermissions.workspaceAccess)
  const showInvoicingNav =
    platformAdmin || hasAnyCap(caps, ClientPermissions.invoicesAccess)
  const showLeavesNav =
    platformAdmin || hasAnyCap(caps, LeavePermissions.access)
  // Supervisor material request alone must not open the stocks workspace.
  const showStocksNav =
    platformAdmin || hasAnyCap(caps, StockPermissions.workspaceAccess)
  const showStockApprovalsNav =
    platformAdmin || hasAnyCap(caps, StockPermissions.adminApprovalsAccess)
  // Admins manage stocks; supervisors (stock_self_request) raise material requests.
  const showSupervisorMaterialRequest =
    hasCap(caps, StockPermissions.selfRequest) && !showStocksNav
  // Role check kept intentionally (AGENTS.md violation flagged; widening access is a separate decision).
  const roles = snapshot?.roles ?? []
  const showSupervisorGatePass =
    roles.includes('Supervisor') &&
    (hasCap(caps, GatePassPermissions.selfRequest) || platformAdmin)
  const showAdminGatePass = platformAdmin || hasCap(caps, GatePassPermissions.approve)
  const showHrGatePass = platformAdmin || hasCap(caps, GatePassPermissions.issue)
  const showStaffPersonalDetailsNav =
    platformAdmin || hasAnyCap(caps, StaffPersonalDetailsPermissions.access)
  const showStaffPersonalDetailsImportNav =
    platformAdmin || hasAnyCap(caps, StaffPersonalDetailsPermissions.importAccess)
  const showInterviewIntakeNav =
    platformAdmin || hasAnyCap(caps, InterviewIntakePermissions.access)

  const showAdminSearch = canAdminRecruitment || platformAdmin
  const showClientIdentityNav =
    platformAdmin || hasCap(caps, ClientPermissions.identityManage)
  const showAdminContracts =
    platformAdmin || hasCap(caps, ClientPermissions.contractsManage)
  const showAccountsContracts =
    platformAdmin ||
    hasCap(caps, ClientPermissions.contractsRead) ||
    hasCap(caps, ClientPermissions.contractsManage)
  const showSalesNav =
    isSalesFeatureEnabled() &&
    (platformAdmin || hasAnyCap(caps, SalesPermissions.access))
  const showStaffOperations = platformAdmin
  const showContactInquiries = platformAdmin
  const showNotificationRulesNav = platformAdmin

  const showClientApplicants = hasCap(caps, 'view_client_applicants')
  const isClientOnly =
    showClientApplicants &&
    !canAccessApplicants &&
    !canManageJobPosts &&
    !canAdminRecruitment

  const showProOnboarding =
    !isClientOnly &&
    proOnboardingWorkspaceAllowed(capSet, snapshot?.roles ?? [])

  const showProExit =
    !isClientOnly && proExitWorkspaceAllowed(capSet, snapshot?.roles ?? [])
  const showProWorkforceDirectory =
    !isClientOnly &&
    proEmployeeDirectoryNavAllowed(capSet, snapshot?.roles ?? [])
  const showProDeskAdmin =
    !isClientOnly && isProDeskSuperAdmin(snapshot?.roles ?? [])

  const showAdminNav =
    showTeam ||
    showAccessControl ||
    showAdminSearch ||
    (showClientIdentityNav && !showSalesNav) ||
    (showAdminContracts && !showSalesNav) ||
    showStaffOperations ||
    showContactInquiries ||
    showNotificationRulesNav

  return {
    showJobPostsNav,
    showApplicantsNav,
    showTalentPoolNav,
    showWalkInNav,
    walkInListedUnderRecruitment,
    showRecruitmentSection,
    showVisaOnboardingNav,
    showWorkforceDirectory,
    showPurgedEmployees,
    showStaffing,
    showTeam,
    showAccessControl,
    showPayroll,
    showPayrollAdmin,
    showAccountsWorkspace,
    showFinanceNav,
    showInvoicingNav,
    showTeamAttendance,
    showAttendanceNotifications,
    showTeamAvailability,
    showSupervisorStaffDocs,
    showSupervisorAppSignIns: false,
    showSelfAttendanceReview,
    showLeavesNav,
    showSupervisorMaterialRequest,
    showSupervisorGatePass,
    showAdminGatePass,
    showHrGatePass,
    showStocksNav,
    showStockApprovalsNav,
    showNotificationRulesNav,
    showStaffPersonalDetailsNav,
    showStaffPersonalDetailsImportNav,
    showInterviewIntakeNav,
    showAdminSearch,
    showClientIdentityNav,
    showAdminContracts,
    showAccountsContracts,
    showSalesNav,
    showStaffOperations,
    showContactInquiries,
    showClientApplicants,
    showProOnboarding,
    showProExit,
    showProWorkforceDirectory,
    showProDeskAdmin,
    showAdminNav,
    showAccountSection: true,
    showMyAccessNav: true,
    isClientOnly,
    isThinAdmin: isThinAdmin(snapshot?.roles ?? []),
    isDocumentFilingOnly: isDocumentFilingOnly(capSet),
    showDocumentFilingSubmit: hasCap(caps, 'document_filing_submit'),
    showDocumentFilingAdmin:
      platformAdmin ||
      hasCap(caps, 'document_filing_manage') ||
      hasCap(caps, 'document_filing_review'),
  }
}

export { proOnboardingWorkspaceAllowed }
